// Lightweight fetch wrapper.
//
// - Reads auth token from useAuthStore (pinia) and attaches it as
//   `Authorization: Bearer <token>` on every request.
// - On 401, clears the auth store and redirects to /admin/login (preserving
//   the original path as `?next=`).
// - 204 → undefined. 2xx with body → parsed JSON. Non-2xx → throws ApiError.
import { useAuthStore } from '../stores/auth'
import { router } from '../router'

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message)
  }
}

export async function api<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const auth = useAuthStore()
  // Only set content-type when there's actually a body. Fastify's JSON parser
  // rejects requests with `content-type: application/json` and an empty body
  // ("Body cannot be empty when content-type is set to 'application/json'"),
  // which used to break every DELETE and the bodyless POST /auth/logout.
  const headers: Record<string, string> = {
    ...((init?.headers as Record<string, string>) ?? {}),
  }
  if (init?.body != null) headers['content-type'] = 'application/json'
  if (auth.token) headers['authorization'] = `Bearer ${auth.token}`

  const res = await fetch(path, { ...init, headers })

  if (res.status === 401) {
    // Token rejected — clear local state and bounce to login.
    auth.clear()
    const current = router.currentRoute.value.fullPath
    if (current && !current.startsWith('/admin/login')) {
      router.push({ path: '/admin/login', query: { next: current } })
    }
  }

  if (res.status === 204) return undefined as T
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const msg = (body as { message?: string } | null)?.message ?? `${res.status} ${res.statusText}`
    throw new ApiError(res.status, body, msg)
  }
  return body as T
}

// Multipart helper for image upload (don't set content-type; browser sets the boundary).
export async function apiUpload<T = unknown>(path: string, formData: FormData): Promise<T> {
  const auth = useAuthStore()
  const headers: Record<string, string> = {}
  if (auth.token) headers['authorization'] = `Bearer ${auth.token}`

  const res = await fetch(path, { method: 'POST', body: formData, headers })
  if (res.status === 401) {
    auth.clear()
    router.push({ path: '/admin/login' })
  }
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const msg = (body as { message?: string } | null)?.message ?? `${res.status} ${res.statusText}`
    throw new ApiError(res.status, body, msg)
  }
  return body as T
}
